import { useEffect, useState } from 'react';
import { plural, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Box, Button, MenuItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorOutline } from '@material-ui/icons';
import _ from 'lodash';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import { isEmailValid, MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import {
    ADMIN_ONLY_ROLES_KEY_VALUE,
    USER_EMAIL_ADDRESSES_FIELD,
    USER_ROLE,
    USER_ROLE_FIELD,
    USER_STATUS_FIELD,
    USER_STATUS_INVITED_OPTION,
} from './UserConstants';
import { useSMTPEnabled } from '../../../stores/EnvironmentStore';
import { useFormQueryService } from '../../../services/FormQueryService';
import { splitEmailString } from '../../../utilities/StringUtilities';
import { useDataService } from '../../../services/ApiDataService';
import NgpSelect from '../../form/NgpSelect';
import NgpTextField from '../../form/NgpTextField';
import NgpSMTPGuard from '../../form/NgpSMTPGuard';
import { RowFlexLayout } from '../../layout/Flex';

const apiUrl = '/ipm/v1/admin/users/';
const emailApiUrl = '/ipm/v1/email/invite';

const useStyles = makeStyles((theme) => ({
    inviteRoleSelect: {
        width: 200,
    },
    dialogTitleContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,
    },
    dialogTitleLeft: {
        ...theme.mixins.flex.grow(1),
    },
    dialogTitleRight: {
        textAlign: 'right',
        fontSize: '14px',
    },
    smtpDisabledContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,
        '& svg': {
            marginRight: 12,
        },
    },
    error: {
        color: theme.palette.error.dark,
    },
}));

function sendEmails(dataService, emails, role) {
    const {
        location: { host, origin },
    } = window;
    return Promise.all(
        _.map(emails, (email) => {
            return dataService.post(emailApiUrl, {
                origin,
                host,
                role,
                toEmail: email,
            });
        })
    );
}

function getInvalidEmails(emails) {
    if (!emails) {
        return false;
    }

    return _.filter(splitEmailString(emails), (email) => !isEmailValid(email));
}

export default function UserInvite({ refresherFunc }) {
    const classes = useStyles();
    const dataService = useDataService();
    const formQueryService = useFormQueryService();
    const [showDialog, setShowDialog] = useState(false);
    const [sendError, setSendError] = useState();
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: {
            [USER_ROLE_FIELD]: USER_ROLE,
            [USER_EMAIL_ADDRESSES_FIELD]: '',
        },
    });
    const { handleSubmit, formState, getValues, setError } = methods;

    const [smtpEnabled] = useSMTPEnabled();
    const [invitesPending, setInvitesPending] = useState(0);

    const handleShowDialog = () => setShowDialog(true);
    const handleFormSubmit = async (dataToSave = {}) => {
        const invalidEmails = getInvalidEmails(getValues(USER_EMAIL_ADDRESSES_FIELD));

        if (invalidEmails.length > 0) {
            setError(USER_EMAIL_ADDRESSES_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: i18n._(
                    plural(invalidEmails.length, {
                        one: `${_.first(invalidEmails)} is not a valid email address`,
                        other: `${invalidEmails.join(', ')} are not valid email addresses`,
                    })
                ),
            });

            return;
        }

        try {
            setSendError(undefined);
            await sendEmails(
                dataService,
                splitEmailString(dataToSave[USER_EMAIL_ADDRESSES_FIELD]),
                dataToSave[USER_ROLE_FIELD]
            );

            if (_.isFunction(refresherFunc)) {
                refresherFunc();
            }

            setShowDialog(false);
        } catch (e) {
            const userValidationError = e?.responseBody?.validations?.userName?.message;

            if (userValidationError) {
                setSendError(userValidationError);
            } else {
                setSendError(String(e));
            }
        }
    };

    const handleCleanup = () => {
        setSendError(null);
    };

    useEffect(() => {
        if (showDialog) {
            formQueryService
                .queryAPI(`${apiUrl}/count`, { [USER_STATUS_FIELD]: USER_STATUS_INVITED_OPTION })
                .then((countResponse) => {
                    setInvitesPending(countResponse.total);
                });
        }

        // Just need to run this when the showDialog toggles on.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDialog]);

    const dialogTitle = (
        <div className={classes.dialogTitleContainer}>
            <div className={classes.dialogTitleLeft}>
                <Trans>Invite New Users</Trans>
            </div>
            <div className={classes.dialogTitleRight}>
                {invitesPending} <Trans>Invites Pending</Trans>
            </div>
        </div>
    );

    return (
        <>
            <Button onClick={handleShowDialog} variant="contained" color="primary">
                <Trans>Invite New Users</Trans>
            </Button>
            <NgpTwoButtonDialog
                title={dialogTitle}
                confirmButtonText={<Trans>Send</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
                open={showDialog}
                setOpen={setShowDialog}
                confirmButtonDisabled={!formState.isValid || !smtpEnabled}
                showConfirmButton={smtpEnabled}
                callerWillCloseOnConfirm
                onConfirm={handleSubmit(handleFormSubmit)}
                closeInTitleBar={false}
                onCloseFunc={handleCleanup}
            >
                <NgpSMTPGuard message={<Trans>The SMTP server will need to be configured to send invite emails</Trans>}>
                    <FormProvider {...methods}>
                        <NgpForm
                            initialized
                            loading={false}
                            showSave={false}
                            formPadding={false}
                            showRequiredFieldsTip={false}
                            submitFunction={handleFormSubmit}
                        >
                            <FormSection>
                                {sendError && (
                                    <RowFlexLayout vCenter className={classes.error}>
                                        <ErrorOutline color="inherit" />
                                        <Typography variant="subtitle2" component={Box} ml={1} color="inherit">
                                            {sendError}
                                        </Typography>
                                    </RowFlexLayout>
                                )}

                                <NgpSelect
                                    fieldName={USER_ROLE_FIELD}
                                    label={<Trans>New User(s) Role</Trans>}
                                    className={classes.inviteRoleSelect}
                                    required
                                >
                                    {_.map(ADMIN_ONLY_ROLES_KEY_VALUE, (value, key) => (
                                        <MenuItem key={key} value={key}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>

                                <NgpTextField
                                    fieldName={USER_EMAIL_ADDRESSES_FIELD}
                                    label={<Trans>Email Address(es) separated by a semicolon, comma, or space</Trans>}
                                    required
                                    inputProps={{ size: 85 }}
                                />
                            </FormSection>
                        </NgpForm>
                    </FormProvider>
                </NgpSMTPGuard>
            </NgpTwoButtonDialog>
        </>
    );
}
